import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { IncidentLog, IncidentLogFormData } from '../models/IncidentLog';

export class IncidentLogService {
  private static instance: IncidentLogService;
  private incidentsCollection = collection(db, 'incidents');

  private constructor() {}

  static getInstance(): IncidentLogService {
    if (!IncidentLogService.instance) {
      IncidentLogService.instance = new IncidentLogService();
    }
    return IncidentLogService.instance;
  }

  async createIncidentLog(
    companyId: string,
    userId: string,
    userName: string,
    data: IncidentLogFormData
  ): Promise<IncidentLog> {
    const incidentRef = doc(this.incidentsCollection);
    const now = new Date();

    // Generate incident number
    const incidentNumber = await this.generateIncidentNumber(companyId);

    const incident: IncidentLog = {
      id: incidentRef.id,
      companyId,
      sessionId: data.sessionId,
      incidentNumber,
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
      incidentDate: data.incidentDate,
      incidentTime: data.incidentTime,
      location: {
        name: data.location,
        address: data.locationAddress,
      },
      involvedStaff: data.involvedStaff.map((name) => ({ name })),
      incidentType: data.incidentType,
      description: data.description,
      notes: data.notes,
      status: 'draft',
      priority: data.priority,
    };

    await setDoc(incidentRef, {
      ...incident,
      createdAt: Timestamp.fromDate(incident.createdAt),
      updatedAt: Timestamp.fromDate(incident.updatedAt),
      incidentDate: Timestamp.fromDate(incident.incidentDate),
    });

    return incident;
  }

  private async generateIncidentNumber(companyId: string): Promise<string> {
    const year = new Date().getFullYear();
    const q = query(
      this.incidentsCollection,
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    let nextNumber = 1;

    if (!querySnapshot.empty) {
      const lastIncident = querySnapshot.docs[0].data();
      const lastNumber = lastIncident.incidentNumber.split('-').pop();
      if (lastNumber) {
        nextNumber = parseInt(lastNumber) + 1;
      }
    }

    return `INC-${year}-${String(nextNumber).padStart(5, '0')}`;
  }

  async getIncidentLog(incidentId: string): Promise<IncidentLog | null> {
    const incidentRef = doc(this.incidentsCollection, incidentId);
    const incidentSnap = await getDoc(incidentRef);

    if (!incidentSnap.exists()) {
      return null;
    }

    const data = incidentSnap.data();
    return {
      ...data,
      id: incidentSnap.id,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      incidentDate: data.incidentDate.toDate(),
    } as IncidentLog;
  }

  async getIncidentLogsByCompany(
    companyId: string,
    limitCount: number = 50
  ): Promise<IncidentLog[]> {
    const q = query(
      this.incidentsCollection,
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        incidentDate: data.incidentDate.toDate(),
      } as IncidentLog;
    });
  }

  async updateIncidentLog(
    incidentId: string,
    updates: Partial<IncidentLog>
  ): Promise<void> {
    const incidentRef = doc(this.incidentsCollection, incidentId);
    await updateDoc(incidentRef, {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async updateStatus(
    incidentId: string,
    status: IncidentLog['status']
  ): Promise<void> {
    const incidentRef = doc(this.incidentsCollection, incidentId);
    await updateDoc(incidentRef, {
      status,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async linkSession(incidentId: string, sessionId: string): Promise<void> {
    const incidentRef = doc(this.incidentsCollection, incidentId);
    await updateDoc(incidentRef, {
      sessionId,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }
}

// Singleton instance
export const incidentLogService = IncidentLogService.getInstance();
